import { useState, useCallback, useEffect, Fragment } from 'react';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { Alert } from '@material-ui/lab';
import {
    Box, Button, Card, CardContent, CardHeader, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, IconButton, Paper, Snackbar, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, makeStyles
} from '@material-ui/core';
import dateToString from '../utils/dateToString';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';
import StarIcon from '@material-ui/icons/Star';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import WarningIcon from '@material-ui/icons/Warning';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';

// The same three states a contestant picks from on the availability form
const NOT_AVAILABLE = 0, AVAILABLE = 1, PREFERRED = 2;

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// both header rows stay visible while scrolling, so the second one needs to know
// how tall the first one is
const HEAD_ROW_HEIGHT = 36;

// how many matches can be run at the same time
const MAX_PARALLEL_MATCHES = 3;

// a flat colour wash, as a background image so that it can sit on top of the
// opaque background a sticky cell needs
const tint = (rgb, alpha) => `linear-gradient(rgba(${rgb}, ${alpha}), rgba(${rgb}, ${alpha}))`;

const useStyles = makeStyles(theme => ({
    tableContainer: {
        maxHeight: 'calc(100vh - 280px)',
    },
    // the match column stays put while the slots scroll sideways
    matchCell: {
        position: 'sticky',
        left: 0,
        zIndex: 3,
        minWidth: 260,
        backgroundColor: theme.palette.background.paper,
        borderRight: `1px solid ${theme.palette.divider}`,
    },
    headMatchCell: {
        zIndex: 5,
    },
    headCell: {
        backgroundColor: theme.palette.background.paper,
        textAlign: 'center',
        whiteSpace: 'nowrap',
        height: HEAD_ROW_HEIGHT,
        padding: theme.spacing(0, 1),
    },
    // the header rows stack below each other instead of on top of each other
    slotHeadCell: {
        top: HEAD_ROW_HEIGHT,
    },
    tallyHeadCell: {
        top: HEAD_ROW_HEIGHT * 2,
        color: theme.palette.text.secondary,
    },
    tallyShort: {
        color: theme.palette.error.main,
        fontWeight: 500,
    },
    dayCell: {
        textAlign: 'center',
        whiteSpace: 'nowrap',
        borderLeft: `1px solid ${theme.palette.divider}`,
    },
    slotCell: {
        textAlign: 'center',
        cursor: 'pointer',
        userSelect: 'none',
        padding: theme.spacing(0.5),
        minWidth: 64,
    },
    readOnlyCell: {
        cursor: 'default',
    },
    // the shades are painted as an image rather than a background colour, so that
    // they tint the sticky cells without making them see-through
    // a slot at least one host has been picked for
    hostColumn: {
        backgroundImage: tint('255, 255, 255', 0.07),
    },
    // a slot no host can make, so no match can be run in it
    noHostColumn: {
        backgroundImage: tint('120, 20, 20', 0.45),
    },
    selected: {
        backgroundImage: tint('255, 255, 255', 0.26),
    },
    dayStart: {
        borderLeft: `1px solid ${theme.palette.divider}`,
    },
    notAvailable: {
        color: theme.palette.error.main,
    },
    available: {
        color: theme.palette.success.main,
    },
    preferred: {
        color: '#ffc107',
    },
    contestantRow: {
        backgroundImage: tint('0, 0, 0', 0.18),
    },
    // the sticky column is opaque, so it needs the row's shade painted on it too
    contestantCell: {
        paddingLeft: theme.spacing(7),
        backgroundImage: tint('0, 0, 0', 0.18),
    },
    dependencyIcon: {
        color: theme.palette.warning.main,
        marginLeft: theme.spacing(0.5),
        verticalAlign: 'middle',
    },
    noResponseIcon: {
        color: theme.palette.text.secondary,
        marginLeft: theme.spacing(0.5),
        verticalAlign: 'middle',
    },
    sectionCell: {
        backgroundColor: theme.palette.background.default,
    },
    count: {
        fontWeight: 500,
    }
}));

function dayToString(day) {
    return `${day} (${WEEKDAYS[new Date(day).getUTCDay()]})`;
}

const StateIcon = ({ state, classes }) => {
    const Icon = [CloseIcon, CheckIcon, StarIcon][state];
    const className = [classes.notAvailable, classes.available, classes.preferred][state];
    return <Icon fontSize='small' className={className} />;
};

const SchedulingPage = () => {
    const classes = useStyles();
    const history = useHistory();
    const route = useRouteMatch();
    const formId = route.params.formId;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errorMessages, setErrorMessages] = useState(null);
    const [expanded, setExpanded] = useState([]);
    // one entry per picked cell, as `${row}|${dayIndex}-${slotIndex}`
    const [selection, setSelection] = useState([]);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [exported, setExported] = useState(null);
    // a finalized form shows what was finalized until the schedule is planned again
    const [live, setLive] = useState(false);

    const getScheduling = useCallback(async (live) => {
        const response = await fetch(
            `/api/admin/availability-form/${formId}/scheduling${live ? '?live=true' : ''}`,
            { credentials: 'include' }
        );
        if (response.status !== 200) return setErrorMessages(['Unexpected error']);
        const result = await response.json();
        if (result.status === 'FAILED') return setErrorMessages(result.body.errorMessages);
        setData(result.body);
        // the allocation belongs to the form, so pick up where anyone left off
        setSelection(result.body.schedule?.selection ?? []);
    }, [formId]);

    useEffect(() => {
        getScheduling(live).then(() => setLoading(false));
    }, [getScheduling, live]);

    const save = useCallback(async (selection, finalize) => {
        setSaving(true);
        const response = await fetch(`/api/admin/availability-form/${formId}/schedule`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ selection, finalize })
        });
        setSaving(false);
        if (response.status !== 200) return setMessage({ severity: 'error', text: 'Unexpected error' });
        const result = await response.json();
        if (result.status === 'FAILED') {
            return setMessage({ severity: 'error', text: result.body.errorMessages.join(' ') });
        }
        if (!finalize) return setMessage({ severity: 'success', text: 'Draft saved' });
        // finalizing writes times and hosts onto the matches and records the table
        // as it stands, so go back to reading that record
        setLive(false);
        await getScheduling(false);
        const scheduled = result.body.applied?.scheduled?.length ?? 0;
        setMessage({
            severity: 'success',
            text: `Finalized: ${scheduled} match${scheduled === 1 ? '' : 'es'} given a time and hosts`
        });
    }, [formId, getScheduling]);

    const toggle = (list, value) => list.indexOf(value) === -1
        ? [...list, value]
        : list.filter(entry => entry !== value);

    if (loading) return <Box align='center' p={3}><CircularProgress /></Box>;

    if (errorMessages) return (
        <Card>
            <CardHeader title='Match scheduling' />
            <CardContent>
                {errorMessages.map((errorMessage, index) => <Typography key={index}>{errorMessage}</Typography>)}
                <Box mt={2}>
                    <Button color='primary' onClick={() => history.push('/admin/availability')}> Back to availability forms </Button>
                </Box>
            </CardContent>
        </Card>
    );

    const { form, matches, hosts, availabilities, schedule } = data;

    // every (day, time slot) pair, in the order they are shown
    const columns = form.days.flatMap((day, dayIndex) => form.timeSlots.map((timeSlot, slotIndex) => ({
        key: `${dayIndex}-${slotIndex}`,
        day,
        timeSlot,
        dayIndex,
        slotIndex,
        firstOfDay: slotIndex === 0
    })));

    const stateOf = (username, column) =>
        availabilities[username]?.slots?.[column.dayIndex]?.[column.slotIndex] ?? AVAILABLE;

    // what was finalized is a record, and is only editable once planned again
    const readOnly = data.finalized === true;

    const isSelected = (row, column) => selection.indexOf(`${row}|${column.key}`) !== -1;
    // a match is played once, so picking a cell for it moves its pick rather than
    // adding a second one. A host can run more than one slot, so their picks add
    // up instead.
    const onToggleCell = (row, column) => readOnly || setSelection(selection => {
        const key = `${row}|${column.key}`;
        if (selection.indexOf(key) !== -1) return selection.filter(entry => entry !== key);
        if (row.startsWith('host-')) return [...selection, key];
        return [...selection.filter(entry => !entry.startsWith(`${row}|`)), key];
    });

    // a slot only works for a match if nobody who could play it is unavailable
    const worksFor = (contestants, column) => contestants.every(username => stateOf(username, column) !== NOT_AVAILABLE);
    const preferredCount = (contestants, column) =>
        contestants.filter(username => stateOf(username, column) === PREFERRED).length;

    const hostColumns = new Set(
        selection
            .filter(entry => entry.startsWith('host-'))
            .map(entry => entry.split('|')[1])
    );

    // nothing can be run in a slot every host said no to
    const hostlessColumns = new Set(
        hosts.length === 0 ? [] : columns
            .filter(column => hosts.every(username => stateOf(username, column) === NOT_AVAILABLE))
            .map(column => column.key)
    );

    // picking a slot somebody cannot make is allowed, but it is worth saying so
    const clashes = matches.filter(match => columns.some(column =>
        isSelected(`match-${match.matchId}`, column) && !worksFor(match.potentialContestants, column)
    ));
    const hostClashes = hosts.filter(username => columns.some(column =>
        isSelected(`host-${username}`, column) && stateOf(username, column) === NOT_AVAILABLE
    ));

    // where each match ended up, taking the earliest pick if there are several
    const assignment = {};
    matches.forEach(match => {
        const column = columns.find(column => isSelected(`match-${match.matchId}`, column));
        if (column != null) assignment[match.matchId] = column;
    });
    const columnIndex = column => column.dayIndex * form.timeSlots.length + column.slotIndex;
    const hostsOf = column => hosts.filter(username => isSelected(`host-${username}`, column));
    const slotName = column => `${column.day} ${column.timeSlot}`;

    // what has been put into each slot, shown as a running tally in the header
    const tallies = {};
    columns.forEach(column => {
        tallies[column.key] = {
            matches: matches.filter(match => isSelected(`match-${match.matchId}`, column)).length,
            hosts: hostsOf(column).length
        };
    });

    // leaving a match unplaced is fine, the rest is not
    const issues = [];
    matches.forEach(match => {
        const column = assignment[match.matchId];
        if (column == null) return;
        if (hostsOf(column).length === 0) {
            issues.push(`${matchName(match)} has no host at ${slotName(column)}`);
        }
        (match.dependencies ?? []).forEach(dependency => {
            const dependencyColumn = assignment[dependency.matchId];
            if (dependencyColumn == null) {
                issues.push(`${matchName(match)} is placed at ${slotName(column)}`
                    + ` but ${dependency.label} is not scheduled`);
                return;
            }
            if (columnIndex(dependencyColumn) >= columnIndex(column)) {
                issues.push(`${matchName(match)} at ${slotName(column)} is not after ${dependency.label}`
                    + ` at ${slotName(dependencyColumn)}, which it needs the result of`);
            }
        });
        // nobody can run a match they might be playing in
        hostsOf(column).forEach(username => {
            if (match.potentialContestants.indexOf(username) !== -1) {
                issues.push(`${username} is hosting ${slotName(column)}, where they could be playing ${matchName(match)}`);
            }
        });
    });

    // a contestant plays at most one match a day, counting the matches they might
    // end up in
    matches.forEach((match, index) => {
        const column = assignment[match.matchId];
        if (column == null) return;
        matches.slice(index + 1).forEach(other => {
            const otherColumn = assignment[other.matchId];
            if (otherColumn == null || otherColumn.dayIndex !== column.dayIndex) return;
            const shared = match.potentialContestants.filter(username =>
                other.potentialContestants.indexOf(username) !== -1);
            if (shared.length === 0) return;
            issues.push(`${shared.join(', ')} could have to play ${matchName(match)} and ${matchName(other)}`
                + ` on the same day, ${column.day}`);
        });
    });

    // one section per tournament, keeping the order the matches came in
    const groups = [];
    for (const match of matches) {
        let group = groups.find(group => group.tournamentId === match.tournamentId);
        if (group == null) {
            group = { tournamentId: match.tournamentId, tournamentName: match.tournamentName, matches: [] };
            groups.push(group);
        }
        group.matches.push(match);
    }

    const cellClass = (row, column, extra) => [
        classes.slotCell,
        readOnly ? classes.readOnlyCell : '',
        column.firstOfDay ? classes.dayStart : '',
        hostColumns.has(column.key) ? classes.hostColumn : '',
        hostlessColumns.has(column.key) ? classes.noHostColumn : '',
        isSelected(row, column) ? classes.selected : '',
        extra ?? ''
    ].join(' ');

    return (
        <>
            <Box mb={2}>
                <Typography variant='h4'>Schedule matches</Typography>
                <Typography color='textSecondary'>
                    {`${form.name} · ${form.startDate} to ${form.endDate}`}
                </Typography>
            </Box>

            {readOnly &&
                <Box mb={2}>
                    <Alert
                        severity='info'
                        action={
                            <Button color='inherit' size='small' onClick={() => setLive(true)}>
                                Plan again
                            </Button>
                        }
                    >
                        {`Finalized ${schedule?.finalizedAt ? dateToString(schedule.finalizedAt) : ''}. This is the`
                            + ` schedule as it was decided, so it does not follow the tournaments on.`}
                    </Alert>
                </Box>
            }
            {!readOnly && schedule?.finalizedAt != null &&
                <Box mb={2}>
                    <Alert
                        severity='warning'
                        action={
                            <Button color='inherit' size='small' onClick={() => setLive(false)}>
                                Back to the record
                            </Button>
                        }
                    >
                        Planning against the tournaments as they stand now. Finalizing replaces the
                        finalized schedule.
                    </Alert>
                </Box>
            }

            <Box mb={2} display='flex' flexWrap='wrap' alignItems='center'>
                <Box display='flex' alignItems='center' mr={3}>
                    <StateIcon state={NOT_AVAILABLE} classes={classes} />
                    <Typography variant='body2'>Not available</Typography>
                </Box>
                <Box display='flex' alignItems='center' mr={3}>
                    <StateIcon state={AVAILABLE} classes={classes} />
                    <Typography variant='body2'>Available</Typography>
                </Box>
                <Box display='flex' alignItems='center' mr={3}>
                    <StateIcon state={PREFERRED} classes={classes} />
                    <Typography variant='body2'>Preferred</Typography>
                </Box>
                <Typography variant='body2' color='textSecondary'>
                    A number is how many of the potential contestants prefer that slot, and it only appears
                    where all of them are available.
                </Typography>
            </Box>

            <TableContainer component={Paper} className={classes.tableContainer}>
                <Table size='small' stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell rowSpan={3} className={`${classes.matchCell} ${classes.headMatchCell}`}>Match</TableCell>
                            {form.days.map(day => (
                                <TableCell
                                    key={day}
                                    colSpan={form.timeSlots.length}
                                    className={`${classes.headCell} ${classes.dayCell}`}
                                >
                                    {dayToString(day)}
                                </TableCell>
                            ))}
                        </TableRow>
                        <TableRow>
                            {columns.map(column => (
                                <TableCell
                                    key={column.key}
                                    className={[
                                        classes.headCell,
                                        classes.slotHeadCell,
                                        column.firstOfDay ? classes.dayStart : '',
                                        hostColumns.has(column.key) ? classes.hostColumn : '',
                                        hostlessColumns.has(column.key) ? classes.noHostColumn : ''
                                    ].join(' ')}
                                >
                                    {column.timeSlot}
                                </TableCell>
                            ))}
                        </TableRow>
                        <TableRow>
                            {columns.map(column => {
                                const tally = tallies[column.key];
                                const overloaded = tally.matches > MAX_PARALLEL_MATCHES;
                                return (
                                    <TableCell
                                        key={column.key}
                                        className={[
                                            classes.headCell,
                                            classes.tallyHeadCell,
                                            column.firstOfDay ? classes.dayStart : '',
                                            hostColumns.has(column.key) ? classes.hostColumn : '',
                                            hostlessColumns.has(column.key) ? classes.noHostColumn : ''
                                        ].join(' ')}
                                    >
                                        {(tally.matches !== 0 || tally.hosts !== 0) &&
                                            <Tooltip title={`${tally.matches} match${tally.matches === 1 ? '' : 'es'} placed, ${tally.hosts} host${tally.hosts === 1 ? '' : 's'} picked`}>
                                                <Typography
                                                    variant='body2'
                                                    className={overloaded ? classes.tallyShort : undefined}
                                                >
                                                    {tally.matches}
                                                </Typography>
                                            </Tooltip>
                                        }
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {matches.length === 0 &&
                            <TableRow>
                                <TableCell className={classes.matchCell} colSpan={columns.length + 1}>
                                    <Typography color='textSecondary'>No match left to schedule</Typography>
                                </TableCell>
                            </TableRow>
                        }
                        {groups.map(group => (
                            <Fragment key={group.tournamentId}>
                                <TableRow>
                                    <TableCell
                                        className={`${classes.matchCell} ${classes.sectionCell}`}
                                        colSpan={columns.length + 1}
                                    >
                                        <Typography variant='subtitle2'>{group.tournamentName}</Typography>
                                    </TableCell>
                                </TableRow>
                                {group.matches.map(match => {
                                    const row = `match-${match.matchId}`;
                                    const isExpanded = expanded.indexOf(match.matchId) !== -1;
                                    return (
                                        <Fragment key={match.matchId}>
                                            <TableRow hover>
                                                <TableCell
                                                    className={classes.matchCell}
                                                    onClick={() => setExpanded(expanded => toggle(expanded, match.matchId))}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <Box display='flex' alignItems='center'>
                                                        <IconButton size='small'>
                                                            {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                                        </IconButton>
                                                        <Box ml={1}>
                                                            <Typography variant='body2'>
                                                                {matchName(match)}
                                                                {(match.dependencies?.length ?? 0) !== 0 &&
                                                                    <Tooltip title={`Requires: ${match.dependencies.map(dependencyName).join(', ')}`}>
                                                                        <WarningIcon fontSize='small' className={classes.dependencyIcon} />
                                                                    </Tooltip>
                                                                }
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                {columns.map(column => {
                                                    const works = worksFor(match.potentialContestants, column);
                                                    return (
                                                        <TableCell
                                                            key={column.key}
                                                            className={cellClass(row, column)}
                                                            onClick={() => onToggleCell(row, column)}
                                                        >
                                                            <Typography variant='body2' className={classes.count}>
                                                                {works ? preferredCount(match.potentialContestants, column) : ''}
                                                            </Typography>
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                            {isExpanded && match.potentialContestants.map(username => (
                                                <TableRow key={`${match.matchId}-${username}`} className={classes.contestantRow}>
                                                    <TableCell className={`${classes.matchCell} ${classes.contestantCell}`}>
                                                        <Typography variant='body2'>
                                                            {username}
                                                            {availabilities[username]?.submitted === false &&
                                                                <Tooltip title='Did not fill in the form'>
                                                                    <HelpOutlineIcon fontSize='small' className={classes.noResponseIcon} />
                                                                </Tooltip>
                                                            }
                                                        </Typography>
                                                    </TableCell>
                                                    {columns.map(column => (
                                                        <TableCell
                                                            key={column.key}
                                                            className={cellClass(row, column, classes.readOnlyCell)}
                                                        >
                                                            <StateIcon state={stateOf(username, column)} classes={classes} />
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </Fragment>
                                    );
                                })}
                            </Fragment>
                        ))}

                        <TableRow>
                            <TableCell
                                className={`${classes.matchCell} ${classes.sectionCell}`}
                                colSpan={columns.length + 1}
                            >
                                <Typography variant='subtitle2'>Hosts</Typography>
                            </TableCell>
                        </TableRow>
                        {hosts.length === 0 &&
                            <TableRow>
                                <TableCell className={classes.matchCell} colSpan={columns.length + 1}>
                                    <Typography color='textSecondary'>No host filled in this form</Typography>
                                </TableCell>
                            </TableRow>
                        }
                        {hosts.map(username => {
                            const row = `host-${username}`;
                            return (
                                <TableRow hover key={row}>
                                    <TableCell className={classes.matchCell}>
                                        <Typography variant='body2'>{username}</Typography>
                                    </TableCell>
                                    {columns.map(column => (
                                        <TableCell
                                            key={column.key}
                                            className={cellClass(row, column)}
                                            onClick={() => onToggleCell(row, column)}
                                        >
                                            <StateIcon state={stateOf(username, column)} classes={classes} />
                                        </TableCell>
                                    ))}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {issues.length !== 0 &&
                <Box mt={2}>
                    <Alert severity='error'>
                        <Typography variant='body2'>{`${issues.length} issue${issues.length === 1 ? '' : 's'} with this allocation:`}</Typography>
                        {issues.map((issue, index) => (
                            <Typography variant='body2' key={index}>{`· ${issue}`}</Typography>
                        ))}
                    </Alert>
                </Box>
            }
            {clashes.length !== 0 &&
                <Box mt={2}>
                    <Alert severity='warning'>
                        {`Picked a slot that does not work for everybody in: ${clashes.map(match => matchName(match)).join(', ')}`}
                    </Alert>
                </Box>
            }
            {hostClashes.length !== 0 &&
                <Box mt={2}>
                    <Alert severity='warning'>
                        {`Picked a slot a host is not available for: ${hostClashes.join(', ')}`}
                    </Alert>
                </Box>
            }

            <Box mt={2} display='flex' alignItems='center' flexWrap='wrap'>
                {readOnly ?
                    <Box mr={2}>
                        <Button variant='contained' color='primary' onClick={() => setLive(true)}>
                            Plan again
                        </Button>
                    </Box>
                    :
                    <>
                        <Box mr={2}>
                            <Button
                                variant='contained'
                                color='primary'
                                disabled={saving}
                                onClick={() => save(selection, false)}
                            >
                                Save draft
                            </Button>
                        </Box>
                        <Box mr={2}>
                            <Button
                                variant='contained'
                                color='secondary'
                                disabled={saving}
                                onClick={() => save(selection, true)}
                            >
                                Finalize
                            </Button>
                        </Box>
                    </>
                }
                <Typography variant='body2' color='textSecondary'>
                    {`${Object.keys(assignment).length} of ${matches.length} matches placed`}
                    {schedule?.savedAt ? ` · saved ${dateToString(schedule.savedAt)}` : ''}
                    {schedule?.finalizedAt ? ` · finalized ${dateToString(schedule.finalizedAt)}` : ''}
                </Typography>
                <Box ml={2}>
                    <Button
                        onClick={() => setExported(scheduleToText({ form, matches, assignment, hostsOf, columns }))}
                        disabled={Object.keys(assignment).length === 0}
                    >
                        Export for Excel
                    </Button>
                </Box>
                <Box ml={2}>
                    <Button onClick={() => setSelection([])} disabled={readOnly || selection.length === 0}> Clear picks </Button>
                </Box>
                <Box ml={2}>
                    <Button onClick={() => history.push('/admin/availability')}> Back to availability forms </Button>
                </Box>
            </Box>

            <Dialog open={exported != null} onClose={() => setExported(null)} maxWidth='md' fullWidth>
                <DialogTitle>Schedule for Excel</DialogTitle>
                <DialogContent>
                    <TextField
                        value={exported ?? ''}
                        multiline
                        fullWidth
                        variant='outlined'
                        minRows={16}
                        InputProps={{ readOnly: true, style: { fontFamily: 'monospace', fontSize: 13 } }}
                        onFocus={event => event.target.select()}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        color='primary'
                        onClick={() => navigator.clipboard?.writeText(exported)
                            .then(() => setMessage({ severity: 'success', text: 'Schedule copied' }))
                            .catch(() => setMessage({ severity: 'error', text: 'Could not copy, select the text instead' }))}
                    >
                        Copy
                    </Button>
                    <Button onClick={() => setExported(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={message != null} autoHideDuration={4000} onClose={() => setMessage(null)}>
                <Alert onClose={() => setMessage(null)} severity={message?.severity}>{message?.text}</Alert>
            </Snackbar>
        </>
    );
};

// "5-6PM" starts at 17:00, "9:30-10:30AM" at 09:30, "17:00-18:00" at 17:00
function slotStartMinutes(timeSlot) {
    const start = /^\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i.exec(timeSlot ?? '');
    if (start == null) return null;
    let hours = Number(start[1]);
    const minutes = Number(start[2] ?? 0);
    // "5-6PM" only says PM once, at the end
    const meridiems = (timeSlot ?? '').match(/am|pm/gi);
    const meridiem = (start[3] ?? meridiems?.[meridiems.length - 1])?.toLowerCase();
    if (meridiem === 'pm' && hours !== 12) hours += 12;
    if (meridiem === 'am' && hours === 12) hours = 0;
    if (hours > 23 || minutes > 59) return null;
    return hours * 60 + minutes;
}

// Contestants report a quarter of an hour before their match, and the matches
// sharing a slot are called in five minutes apart
const REPORTING_LEAD = 15;
const REPORTING_STAGGER = 5;

function minutesToString(minutes) {
    const wrapped = ((minutes % (24 * 60)) + 24 * 60) % (24 * 60);
    return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`;
}

function dayToDateString(day) {
    const [, month, dayOfMonth] = day.split('-');
    return `${Number(dayOfMonth)}/${Number(month)}`;
}

// A tournament is one division, named by its last character, and the round id is
// that division and the bracket label together, as in "3-W5"
function divisionOf(tournamentName) {
    const id = (tournamentName ?? '').trim().slice(-1);
    return { label: `Div. ${id}`, id };
}

const EXPORT_COLUMNS = ['Date', 'Reporting Time', 'Contestant 1', 'Contestant 2', 'Division', 'Round ID', 'Host(s)'];
const EXPORT_BLANK = new Array(EXPORT_COLUMNS.length).fill('').join('\t');

// The whole schedule as one block of tab separated text, a day at a time, for
// pasting into a spreadsheet
function scheduleToText({ form, matches, assignment, hostsOf, columns }) {
    const lines = [EXPORT_COLUMNS.join('\t'), EXPORT_BLANK];
    form.days.forEach((day, dayIndex) => {
        if (dayIndex !== 0) lines.push(EXPORT_BLANK);
        columns
            .filter(column => column.dayIndex === dayIndex)
            .forEach(column => {
                const slotStart = slotStartMinutes(column.timeSlot);
                matches
                    .filter(match => assignment[match.matchId]?.key === column.key)
                    .forEach((match, index) => {
                        const division = divisionOf(match.tournamentName);
                        lines.push([
                            dayToDateString(day),
                            slotStart == null ? column.timeSlot
                                : minutesToString(slotStart - REPORTING_LEAD + index * REPORTING_STAGGER),
                            match.contestant1 ?? 'TBD',
                            match.contestant2 ?? 'TBD',
                            division.label,
                            match.label ? `${division.id}-${match.label}` : division.id,
                            hostsOf(column).join(', ')
                        ].join('\t'));
                    });
            });
    });
    // days after the last match do not need to be spelled out
    while (lines.length !== 0 && lines[lines.length - 1] === EXPORT_BLANK) lines.pop();
    return lines.join('\n');
}

function dependencyName(dependency) {
    const contestants = dependency.contestant1 != null && dependency.contestant2 != null
        ? ` (${dependency.contestant1} vs ${dependency.contestant2})`
        : '';
    return `${dependency.label}${contestants}`;
}

function matchName(match) {
    const contestants = `${match.contestant1 ?? 'TBD'} vs ${match.contestant2 ?? 'TBD'}`;
    return match.label ? `${match.label}: ${contestants}` : contestants;
}

export default SchedulingPage;
